import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import moment from "moment";
import "moment/locale/en-gb";
import { toast } from "react-toastify";
import { keys } from "lodash";
import {
  DatePicker,
  Button,
  Tag,
} from "antd";
import dayjs from "dayjs";
import {
  getReadySendReports,
  moveFiles,
  deleteFiles,
  runQCApiPost,
  updateTestResult,
  updateReportsSendStatus,
  approveRejectSampleQC,
  getUserList,
  sendReportServer,
  getReportSamplesInfo,
} from "../../../actions/sendReports";
import { fetchTestCategory } from "../../../actions/chemistry";
import SearchSendReports from "./components/SearchSendReports";
import SendReportsPreview from "./components/SendReportsPreview";
import SendReportsConfirmModal from "./components/SendReportsConfirmModal";
import ConfirmationModal from "../../Common/ConfirmationModal";
import {
  downloadFileFromS3,
  getUser,
  pause,
  getPresignedURL,
  formatAntdDateRange,
  getFileFromS3,
} from "../../../utils/helpers";
import { s3AutomationPrivate } from "../../../utils/aws";
import { 
  DEFAULT_DATE_FORMAT, 
  getHistoryDates, 
} from "./utils";
import { ML_SOFTWARE_QC_FLAGS, REPORT_STATUS_FLAGS, convertStatusMapToArrayOptions } from "../../../utils/statusConfig";
import FilterDropdown from "./components/FilterDropdown";

import SendReportsTable from "./components/SendReportsTable";
import ActionPopup from "./components/ActionPopup";
import UnchangedWOModal from "./components/UnchangedWOModal";

import SendReportSummaryModal from "./components/SendReportSummaryModal";
import { postBatchReports } from "../../../actions/batchReports";
import useAbortController from "../../Common/hooks/useAbortController";
import "./SendReportsServer.css";

const SendReportsServer = ({ tab }) => {
  // State management using useState hooks
  const [filesReady, setFilesReady] = useState([]);
  const [loading, setLoading] = useState(false);
  const [skeletonLoading, setSkeletonLoading] = useState(true);
  const [historyDate, setHistoryDate] = useState(getHistoryDates().startDate);
  const [searchValue, setSearchValue] = useState("");
  const [showSendSummary, setShowSendSummary] = useState(false);
  const [sendSummaryData, setSendSummaryData] = useState({
    uploadedAndSent: [],
    failed: []
  });
  const [order, setOrder] = useState("DSC");
  const [counter, setCounter] = useState(0);
  const [previewFile, setPreviewFile] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [selectedReports, setSelectedReports] = useState({
    qcFailed: [],
    qcNotRun: [],
    qcUnknown: [],
    qcPassed: [],
  });
  const [woStatus, setWoStatus] = useState(new Map());
  const [badWoList, setBadWoList] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationType, setConfirmationType] = useState("");
  const [confirmationTitle, setConfirmationTitle] = useState("");
  const [confirmationBodyComponent, setConfirmationBodyComponent] = useState(null);
  const [fromRemoveClick, setFromRemoveClick] = useState(false);
  const [currWoId, setCurrWoId] = useState("");
  const [currPreviewIdx, setCurrPreviewIdx] = useState(-1);
  const [enableEdit, setEnableEdit] = useState(false);
  const [moveCategory, setMoveCategory] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("");
  const [selectedReportType, setSelectedReportType] = useState("");
  const [reportTypeOptions, setReportTypeOptions] = useState([]);
  const [pendingMarkStatus, setPendingMarkStatus] = useState(null);
  // const [selectedQc, setSelectedQc] = useState("");  // NOTE: Uncomment for qc filter
  // const [selectedAiQc, setSelectedAiQc] = useState("");  // NOTE: Uncomment for ai qc filter
  const [selectedMLSoftQc, setSelectedMLSoftQc] = useState("");
  const [user] = useState(getUser());
  const [testCategories, setTestCategories] = useState([{ value: "", label: "All Categories" }]);
  const [testCategoriesOptions, setTestCategoriesOptions] = useState([{ value: "", label: "All Categories" }]);
  const [createByUserOptions, setCreateByUserOptions] = useState([{ value: "", label: "All" }]);
  const [approvedByUserOptions, setApprovedByUserOptions] = useState([{ value: "", label: "All" }]);
  const [selectedCreatedBy, setSelectedCreatedBy] = useState("");
  const [selectedApprovedBy, setSelectedApprovedBy] = useState("");
  const [selectedSentBy, setSelectedSentBy] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [runQcType, setRunQcType] = useState(null);
  const { setNewAbortController } = useAbortController();

  // States for unchanged wo
  const [showUnchangedWOModal, setShowUnchangedWOModal] = useState(false);
  const [unchangedWoList, setUnchangedWoList] = useState([]);

  // Refs
  const datePickerRef = useRef();
  const apiController = useRef(null);
  const selectedRowKeysRef = useRef(selectedRowKeys);

  /**
   * Fetch user list for dropdown
   */
  const fetchUserList = async () => {
    const response = await getUserList();
    if (response.success) {
      const userList = response.results;
      const { createByOptions, approvedByOptions } = userList?.reduce((acc, u) => {
        const label = `${u.first_name} ${u.last_name}`;
        acc.createByOptions.push({ value: u.id, label });
        acc.approvedByOptions.push({ value: u.email, label });
        return acc;
      }, { createByOptions: [], approvedByOptions: [] });

      setCreateByUserOptions([...createByUserOptions, ...createByOptions]);
      setApprovedByUserOptions([...approvedByUserOptions, ...approvedByOptions]);
    } else {
      toast.error(response.message || "Something went wrong!");
    }
  };

  // Main getFilesReady function
  const getFilesReady = useCallback(async (dateInput, searchInput, orderValue, category) => {
    let date = "";
    if (category === "history") {
      date = formatAntdDateRange([dateInput, getHistoryDates(dateInput).endDate]);
    }
    const input = searchInput;
    const files = [];
    const woStatusMap = new Map();
    if (date === "Invalid date" || category !== "history") {
      date = "";
    }

    setLoading(true);
    setSkeletonLoading(true);
    
    const controller = new AbortController();
    abortPrevApiCall(controller);
    const params = {
      date_range: date,
      search: input,
      order: orderValue,
      category,
      test_category_id: selectedCategory,
      filter: selectedFilter,
      report_type: selectedReportType,
      qc_status: "", //selectedQc, // NOTE: Uncomment for qc filter
      ml_qc_status: "", //selectedAiQc, // NOTE: Uncomment for ai qc filter
      ml_soft_qc_result: selectedMLSoftQc,
      created_by: selectedCreatedBy,
      approved_by: selectedApprovedBy,
      sent_by: selectedSentBy,
    };

    const response = await getReadySendReports(params, controller.signal);
    if (response && response.success && response.list.length > 0) {
      response.list.forEach((element) => {
        if (!woStatusMap.has(element.wo_id)) {
          woStatusMap.set(element.wo_id, { wo_id: element.wo_id, reportsLeft: 0, totalReports: 0 });
        }
        const woObj = woStatusMap.get(element.wo_id);
        if (woObj) {
          woObj.reportsLeft += 1;
          woObj.totalReports += 1;
        }
        const pathIdx = element.path.split("/").length - 1;
        const time_stamp = moment(element.time_stamp).format(
          "MM/DD/YYYY, h:mm:ss a",
        );
        const file = {
          path: element.path.split("/")[pathIdx],
          wo: element.wo_id,
          customer: element.customer_id,
          checked: false,
          receivedDate: element.received_date,
          time: time_stamp === "Invalid date" ? "-" : time_stamp,
          created_by: element.created_by,
          type: element.belongs,
          reportID: element.report_id,
          reportIdPk: element.report_id_pk,
          qc: element.ml_soft_qc_result,
          s3Path: element.path,
          approve_status: element.approve_status,
          approved_by: element.approved_by,
          approved_at: element.approved_at ? moment(element.approved_at).format("MM/DD/YYYY, h:mm:ss a") : "",
          sent_by: element.sent_by,
          sent_at: element.sent_at ? moment(element.sent_at).format("MM/DD/YYYY, h:mm:ss a") : "",
          po: element.po ?? "",
          report_type: element.report_type ?? "",
          esv_belongs: element.esv_belongs ?? "",
        };
        files.push(file);
      });

      setFilesReady(files);
      setCounter(0);
      setLoading(false);
      setSkeletonLoading(false);
      setWoStatus(woStatusMap);
      setSearchValue(input);
      setOrder(orderValue);
      setSelectedRowKeys([]);

      // console.log("reports per workorder: ", woStatusMap);
    } else if (!response.canceled) {
      setLoading(false);
      setSkeletonLoading(false);
      setFilesReady(files);
      setCounter(0);
      setSearchValue(input);
      setOrder(orderValue);
      setSelectedRowKeys([]);

      if (!response.success) {
        toast.error("Something went wrong");
      }
    }
  }, [loading, selectedCategory, selectedFilter, selectedReportType, /* selectedQc, selectedAiQc, */ selectedMLSoftQc, selectedCreatedBy, selectedApprovedBy, selectedSentBy]); // eslint-disable-line


  // Update ref when selectedRowKeys changes
  useEffect(() => {
    selectedRowKeysRef.current = selectedRowKeys;
  }, [selectedRowKeys]);

  // When the component mounts, fetch the test categories and user list. This happens when switching tabs
  useEffect(() => {
    fetchTestCategories();
    // fetch user list for dropdown options
    fetchUserList();
    // fetch report type mapping from API
    fetchReportTypeMapping();
  }, []); // eslint-disable-line

  // useEffect to handle filter changes
  useEffect(() => {
    const date = tab === "history" ? historyDate : "";
    getFilesReady(date, searchValue, order, tab);
  }, [selectedCategory, selectedFilter, selectedReportType, /* selectedQc, selectedAiQc, */ selectedMLSoftQc, selectedCreatedBy, selectedApprovedBy, selectedSentBy, historyDate]); // eslint-disable-line


  // Helper functions
  const abortPrevApiCall = (controller) => {
    if (apiController.current) {
      apiController.current.abort();
    }
    apiController.current = controller;
  };


  /**
   * Fetch test categories
   */
  const fetchTestCategories = async () => {
    const { success, results, message } = await fetchTestCategory();
    if (success) {
      const newTestCategoriesOptions = [{ value: "", label: "All Categories" }];
      const newCategoryList = [{ value: "", label: "All Categories" }];
      results.forEach(({ test_category_name, id }) => {
        newTestCategoriesOptions.push({ value: id, label: test_category_name });
        newCategoryList.push({ value: test_category_name, label: test_category_name });
      });
      setTestCategories(newCategoryList);
      setTestCategoriesOptions(newTestCategoriesOptions);
    } else {
      toast.error(message);
    }
  };

  /**
   * Fetch report type mapping from API
   */
  const fetchReportTypeMapping = async () => {
    try {
      const response = await getReadySendReports({ get_info: true });
      if (response.success && response.report_type_mapping) {
        // Build options from the mapping
        const options = [
          { value: "", label: "All" } // Empty string for "All"
        ];
        
        // Add other options (excluding "all" since we use "" for All)
        Object.entries(response.report_type_mapping).forEach(([key, value]) => {
          if (value !== "all") {
            const label = value.charAt(0).toUpperCase() + value.slice(1);
            options.push({ value: key, label });
          }
        });
        
        setReportTypeOptions(options);
      }
    } catch (error) {
      toast.error("Something went wrong while fetching report types.");
    }
  };

  /**
   * handle selecting a row by clicking the checkbox
   */
  const handleChangeCheckbox = useCallback((record) => {
    setFilesReady(prevList => {
      const list = [...prevList];
      const newWoStatus = new Map(woStatus);

      const index = list.findIndex((report) => report.wo === record.wo && report.reportID === record.reportID);
      const report = list[index];

      const woObj = newWoStatus.get(report.wo);
      if (woObj) {
        woObj.reportsLeft = report.checked ? woObj.reportsLeft + 1 : woObj.reportsLeft - 1;
      }

      // Toggle the checked state
      report.checked = !report.checked;

      // Update selectedRowKeys array based on the new checked state
      const rowKey = `${report.wo}-${report.reportID}`;
      let newSelectedRowKeys;

      if (report.checked) {
        // Add to selectedRowKeys if not already present
        if (!selectedRowKeysRef.current.includes(rowKey)) {
          newSelectedRowKeys = [...selectedRowKeysRef.current, rowKey];
        } else {
          newSelectedRowKeys = selectedRowKeysRef.current;
        }
      } else {
        // Remove from selectedRowKeys
        newSelectedRowKeys = selectedRowKeysRef.current.filter(key => key !== rowKey);
      }

      newWoStatus.set(report.wo, woObj);
      list[index] = report;

      // Update all states atomically
      setWoStatus(newWoStatus);
      setSelectedRowKeys(newSelectedRowKeys);
      setCounter(newSelectedRowKeys.length);

      return list;
    });
  }, [woStatus]);


  const removeBadReports = (woObj) => {
    if (!woObj || !woObj.wo_id) return;

    setFilesReady(prevList => {
      const list = [...prevList];
      const newWoStatus = new Map(woStatus);
      const selectedItems = [...selectedRowKeys];

      list.forEach((report) => {
        if (report.wo === woObj.wo_id && report.checked) {
          const woStatusObj = newWoStatus.get(report.wo);
          if (woStatusObj) {
            woStatusObj.reportsLeft += 1;
            newWoStatus.set(report.wo, woStatusObj);
          }
          report.checked = false; // Uncheck the report

          const idx = selectedItems.indexOf(`${report.wo}-${report.reportID}`);
          if (idx > -1) {
            selectedItems.splice(idx, 1);
          }
        }
      });

      // Update all states together
      setSelectedRowKeys(selectedItems);
      setCounter(selectedItems.length);
      setWoStatus(newWoStatus);

      return list;
    });
  }

  const getSendList = () => {
    const sendList = [];

    filesReady.forEach((report) => {
      if (tab === "history") {
        if (report.checked) {
          sendList.push(report);
        }
      } else if (report.checked && woStatus.get(report.wo)?.reportsLeft === 0) {
        sendList.push(report);
      } else if (report.checked && woStatus.get(report.wo)?.reportsLeft > 0) {
        removeBadReports(woStatus.get(report.wo));
      }
    });
    return sendList;
  }

  /**
   * handle search input
   */
  const handleSearchSubmit = useCallback((e, value, updateSearch) => {
    if (updateSearch) {
      setSearchValue(value);
      return;
    }
    if (typeof e.key === "string" && e.key.toLowerCase() === "enter") {
      setSearchValue(value);
    }
    const category = tab;
    const date = category === "history" ? historyDate : "";
    const input = value;
    if (e.key === "Enter") {
      getFilesReady(date, input, "DSC", category);
    }
  }, [tab, historyDate, getFilesReady]);

  /**
   * Filter test category names when typing in the test category filter
   * @param {*} value input value
   */
  const filterByTestCategory = (value) => {
    const category = value || testCategoriesOptions[0].value;
    setSelectedCategory(category);
  };

  /**
   * handle date change
   */
  const handleHistoryDate = useCallback((dateRange) => {
    setHistoryDate(dateRange);
  }, []);

  /**
   * Sort by created by time
   */
  const handleTimeSort = useCallback(async () => {
    const category = tab;
    const input = searchValue;
    const date = category === "history" ? historyDate : "";
    let newOrder = order;
    if (newOrder === "DSC") {
      newOrder = "ASC";
      setOrder(newOrder);
    } else {
      newOrder = "DSC";
      setOrder(newOrder);
    }
    getFilesReady(date, input, newOrder, category);
  }, [tab, searchValue, historyDate, order, getFilesReady]);


  /**
   * download report
   */
  const handleDownload = useCallback(async () => {
    if (skeletonLoading) {
      toast.warning("Please Select a report");
      return;
    }
    const list = filesReady;
    const data = [];
    let downloadedCount = 0;
    // Collect all the S3 paths
    list.forEach((file) => {
      if (file.checked) {
        let path = file.s3Path;
        if (tab === "pending") {
          path = file.s3Path.replace("/pending/", "/available/");
        } else if (tab === "hold") {
          path = file.s3Path.replace("/hold/", "/available/");
        }
        data.push({ path, fileName: file.path });
      }
    });
    if (data.length === 0) {
      toast.warning("Please Select a report");
      return;
    }

    // Fetch all the files' presigned URL
    const presignedData = await getPresignedURL({
      action: "get",
      urls: data.map((item) => ({
        Bucket: s3AutomationPrivate.bucket,
        Region: s3AutomationPrivate.region,
        Name: item.path,
      })),
    });
    // Download files based on the presigned URLs
    if (presignedData.success) {
      for (let i = 0; i < data.length; i++) {
        const { path, fileName } = data[i];
        const downloadResp = await downloadFileFromS3(path, s3AutomationPrivate, fileName, presignedData.result[path]);
        if (!downloadResp.success) {
          toast.error(`Failed to download ${fileName}`); // Show error when download fails
        }
        if (++downloadedCount >= 10) {
          await pause(1500);
          downloadedCount = 0;
        }
      }
    } else {
      toast.error("Failed to fetch the presigned URL");
    }
  }, [skeletonLoading, filesReady, tab]);


  /**
   * handle download button click
   */
  const handleDownloadClick = useCallback(async () => {
    setLoading(true);
    setSkeletonLoading(true);
    await handleDownload();
    setLoading(false);
    setSkeletonLoading(false);
    setSelectedRowKeys([]);
    setCounter(0);
  }, [handleDownload]);

  /**
   * fetch file from s3 for preview
   * @param {string} s3Path file path
   */
  const getPreviewFileFromS3 = async (s3Path, signal) => {
    /**
     * Even though the backend reponse is /pending/, we only have the available and history folder in s3.
     * So we need to replace /pending/ to /available/
     */
    if (tab === "pending") {
      s3Path = s3Path.replace("/pending/", "/available/");
    } else if (tab === "hold") {
      s3Path = s3Path.replace("/hold/", "/available/");
    }

    const controller = new AbortController();
    abortPrevApiCall(controller);
    const response = await getFileFromS3(s3Path, s3AutomationPrivate, false, true, "application/pdf", signal);
    return { ...response };
  };

  /**
   * handle preview sample modal
   */
  const handlePreview = useCallback(async (file, index) => {
    const newAbortController = setNewAbortController();
    
    const [fileResult, samplesResult] = await Promise.all([
      getPreviewFileFromS3(file.s3Path, newAbortController.signal),
      getReportSamplesInfo({ report_id_pk: file.reportIdPk }, newAbortController.signal)
    ]);
    
    const { blob, presignedFailed, aborted: fileAborted } = fileResult;
    const { success, result, message, canceled: sampleInfoAborted } = samplesResult;
    
    if (fileAborted || sampleInfoAborted) {
      return;
    }

    if (blob === "") {
      if (presignedFailed && !fileAborted) {
        toast.error("Failed to fetch the presigned URL");
      } else if (!fileAborted) {
        toast.error("Failed to get the File from AWS S3");
      }
      if (!fileAborted) return;
    } else {
      if (!success || result?.sample_info?.length === 0) {
        toast.error(message);
        return;
      }
      const url = window.URL.createObjectURL(blob);
      const previewObj = {
        ...file,
        url,
        title: file.path.slice(0, -4),
        currPreviewIdx: index,
        sample_info: result.sample_info,
      };
      const userDetails = getUser();
      /** Dont't allow the edit result and re-generate report for pesticide report */
      const enableEdit = !/.*P$/.test(file.reportID) && tab === "available" && userDetails.permissionMap.change_test_result_sendreports;
      setPreviewFile(previewObj);
      setShowPreview(true);
      setCurrWoId(file.wo);
      setCurrPreviewIdx(index);
      setEnableEdit(enableEdit);
    }
  }, [tab]); // eslint-disable-line

  /**
   * toggle preview modal
   */
  const togglePreview = useCallback((previewData) => {
    // Sync the latest sample info from the preview sample table
    const { currPreviewIdx, sample_info } = previewData;

    setFilesReady(prevList => {
      const list = [...prevList];
      if (currPreviewIdx < list.length && list[currPreviewIdx].reportID === previewData.reportID) {
        // Check if the preview index is valid and the report ID matches, then update the sample info
        // We need to update the sample info to sync up the ML QC Approve/Reject status and the report status
        list[currPreviewIdx].sample_info = sample_info;
      }
      return list;
    });

    setShowPreview(false);
    setCurrWoId("");
    setPreviewFile("");
    setEnableEdit(false);
  }, []);

  /**
   * Make an api call to update the test's result value, and set the new test's result
   * @param {*} payload
   * @returns
   */
  const updateReusltApi = async (payload) => {
    const resp = await updateTestResult(payload);
    if (resp.success) {
      toast.success(resp.message ?? "Result updated successfully");
    } else {
      toast.error(resp.message ?? "Something went wrong");
    }
    return resp;
  }

  /**
     * Make an api call to generate a report for the current selected workorder
     * @returns
     */
  const generateReport = async () => {
    const received_date = filesReady[currPreviewIdx].receivedDate;
    const date = moment(new Date()).format(DEFAULT_DATE_FORMAT);
    const list = [{
      wo_id: currWoId,
      is_partial: "True",
    }];

    const resp = await postBatchReports(list, date, null, received_date, true, false, user.id, { action: "Re-generate Report" });
    if (!resp.success) {
      toast.error(resp.message ?? "Something went wrong");
      return resp;
    }
    setShowPreview(false);
    setCurrWoId("");
    setCurrPreviewIdx(-1);
    setPreviewFile("");
    setEnableEdit(false);
    await getFilesReady("", searchValue ?? "", order, tab);
    toast.success("Report generated successfully.");
    return resp;
  }

  /**
     * If the report status filter is either Approved or Hold,
     * when a user selects the opposite status for the selected report,
     * we need to remove the selected report from the report list if the
     * api succeeded and uncheck the checkbox
     * @param {*} data
     * @param {*} message
     */
  const uncheckAndRemoveItem = (data, message) => {
    const email = getUser().email;
    const selectedItems = [...selectedRowKeys];
    if (data.checked) {
      const woObj = woStatus.get(data.wo);
      if (woObj) woObj.reportsLeft -= 1;
      data.checked = false;
      const idx = selectedItems.indexOf(`${data.wo}-${data.reportID}`);
      selectedItems.splice(idx, 1);
    }
    filesReady.splice(data.currPreviewIdx, 1);
    setFilesReady(filesReady);
    setSelectedRowKeys(selectedItems);
    setCounter(selectedItems.length);
    setWoStatus(woStatus);
    setPreviewFile({
      ...data,
      approve_status: data.approve_type,
      approved_by: email,
      approved_at: moment(new Date()).format("MM/DD/YYYY, h:mm:ss a"),
    });
    toast.success(message);
  };

  /**
     * Make an api call to update the selected report send status
     * @param {*} data
     * @returns
     */
  const updateStatusApi = async (data) => {
    const { wo: workorder_id, reportID: report_id } = data;
    const { email: user_email, id } = getUser();
    const payload = {
      wo_report_ids: [{ workorder_id, report_id }],
      new_report_status: data.approve_type,
      user_email,
      user_id: id,
    };
    const response = await updateReportsSendStatus(payload);
    const { success, message } = response;
    
    if (success) {
      // not updated wo report check
      const hasUnchangedWos = response.not_updated_wo_report?.length > 0;
      if (hasUnchangedWos) {
        setUnchangedWoList(response.not_updated_wo_report || []);
        setShowUnchangedWOModal(true);
        return  { success: false, message };
      }

      // update the background state
      if (selectedFilter === "") {
        filesReady[data.currPreviewIdx] = {
          ...filesReady[data.currPreviewIdx],
          approve_status: data.approve_type,
          approved_by: user_email,
          approved_at: moment(new Date()).format("MM/DD/YYYY, h:mm:ss a"),
        };
      } else if (selectedFilter !== data.approve_type) {
        // if filter is applied and user selected a opposite report status,
        // then remove the item from the current list and its related states
        uncheckAndRemoveItem(data, message);
        return { success, message };
      } else {
        // If a user selects a opposite report status and then selete the same status
        // again, then we need to add the item back to the list
        filesReady.splice(data.currPreviewIdx, 0, {
          ...data,
          approve_status: data.approve_type,
          approved_by: user_email,
          approved_at: moment(new Date()).format("MM/DD/YYYY, h:mm:ss a"),
        });
      }
      setFilesReady(filesReady);
      setPreviewFile({ ...data, approve_status: data.approve_type });
      toast.success(message);
    } else {
      toast.error(message);
    }
    return { success, message };
  }

  /**
   * approve/reject with comment
   * @param {*} sample sample
   * @param {string} comment comment
   * @returns 
   */
  const handleApproveRejectSampleQC = async (sample, comment) => {
    const { id } = getUser();
    const {
      tis_pk,
      user_correction,
    } = sample;
    const payload = {
      tis_pk: String(tis_pk),
      user_correction: String(user_correction),
      user_id: id,
      ml_qc_comments: comment || "",
    };
    const response = await approveRejectSampleQC(payload);
    if (response.success) {
      const { message, result } = response;
      /**
       * There are two cases for success
       * 1. ML Result is not empty for the test, so backend will updates the user correction and return a object of result
       * 2. ML Result is empty for the test, backend will returns a empty result list
       */
      if (typeof result === "object" && result !== null && !Array.isArray(result)) {
        toast.success(message);
      } else {
        toast.info(message);
      }
    } else {
      toast.error(response.message);
    }
    return response;
  }

  /**
   * Reset the confirmation popup related states.
   */
  const resetConfirmationPopupStates = useCallback((error_reset = false) => {
    // NOTE: commenting error reset because when error occurs, the confirmation modal doesn't close 
    // can be uncommented if the confirmation modal needed to be opened even after error occurs
    // if (!error_reset) {
      setShowConfirmation(false);
      setConfirmationTitle("");
      setConfirmationBodyComponent(null);
      setFromRemoveClick(false);
      setMoveCategory("");
      setPendingMarkStatus(null);
      setRunQcType(null);
      setConfirmationType("");
    // }

    setLoading(false);
    setSkeletonLoading(false);
  }, [setShowConfirmation, setConfirmationTitle, setConfirmationBodyComponent, setFromRemoveClick, setMoveCategory, setPendingMarkStatus, setRunQcType, setLoading, setSkeletonLoading]);

  /**
   * update report status
   * @param {*} new_report_status 0, 1, 2, 4
   * @returns 
   */
  const batchUpdateReportStatus = async (new_report_status) => {
    setLoading(true);
    const wo_report_ids = [];

    filesReady.forEach((file) => {
      const { wo: workorder_id, reportID: report_id } = file;
      if (file.checked) {
        wo_report_ids.push({ workorder_id, report_id });
      }
    });

    if (wo_report_ids.length < 1) {
      toast.warning("Please Select a report");
      return;
    }
    const { email: user_email, id } = getUser();
    const payload = {
      wo_report_ids,
      new_report_status,
      user_email,
      user_id: id,
    };
    const response = await updateReportsSendStatus(payload);
    const { success, message } = response;

    // not updated wo report check
    const hasUnchangedWos = response.not_updated_wo_report?.length > 0;

    if (success) {
      const input = searchValue;
      const date = tab === "history" ? historyDate : "";
      toast.success(message);
      resetQCState();
      resetConfirmationPopupStates();
      // if success but has unchaged wo
      if (hasUnchangedWos) {
        setUnchangedWoList(response.not_updated_wo_report || []);
        setShowUnchangedWOModal(true);
      }
      await getFilesReady(date, input, order, tab);
    } else {
      toast.error(message);
      resetQCState();
      resetConfirmationPopupStates(true);
    }
    setLoading(false);
  };

  /**
     * Make an api call to delete the selected reports
     */
  const handleRemoveConfirm = async () => {
    const payload = [];
    const category = tab;
    const date = category === "history" ? historyDate : "";
    const input = searchValue;

    // For removal, we want to remove all checked reports regardless of work order completion status
    // So we collect all checked reports directly instead of using getSendList() which filters by work order status
    filesReady.forEach((file) => {
      if (file.checked) {
        payload.push(file.reportID);
      }
    });
    const response = await deleteFiles(payload);
    if (response && response.success) {
      toast.success("Removed Successfully");
      resetConfirmationPopupStates();
      await getFilesReady(date, input, order, category);
    } else {
      toast.error(response.message);
      resetConfirmationPopupStates(true);
    }
  }

  /**
     * Make an api call to move the selected reports to Pending, Hold, or Available.
     */
  const handleMoveConfirm = async () => {
    const category = tab;
    const date = "";
    const order = "DSC";
    const input = searchValue;
    const reportIdList = [];
    const sendList = getSendList();
    sendList.forEach((file) => {
      if (file.checked) {
        reportIdList.push(file.reportID);
      }
    });
    const response = await moveFiles({ report_id_list: reportIdList, category: tab, new_category: moveCategory });
    const tabTitle = `${moveCategory[0]?.toUpperCase()}${moveCategory?.slice(1)}`;
    if (response && response.success) {
      toast.success(`Successfully moved to ${tabTitle}`);
      resetConfirmationPopupStates();
      await getFilesReady(date, input, order, category);
    } else {
      toast.error(response.message);
      resetConfirmationPopupStates(true);
    }
  }

  /**
 * Callback function when users click Confirm for deleting reports or moving repors to pending
 */
  const handleRemovePendingConfirm = async () => {
    setLoading(true);
    if (fromRemoveClick) {
      await handleRemoveConfirm();
    } else {
      await handleMoveConfirm();
    }
  }

  /**
   * Calls api to run workorder qc
   */
  const runWororderQc = useCallback(async (rerun) => {
    setLoading(true);
    
    const reports = [];
  
    filesReady.forEach((file) => {
      const { reportID: report_id } = file;
      if (file.checked) {
        reports.push({ report_id });
      }
    });
  
    if (reports.length < 1) {
      toast.warning("Please Select a report");
      setLoading(false);
      return;
    }
  
    try {
      const { success, message } = await runQCApiPost({ rerun, report_id_list: reports });
      if (success) {
        const input = searchValue;
        const date = tab === "history" ? historyDate : "";
        toast.success(message);
        resetConfirmationPopupStates();
        await getFilesReady(date, input, order, tab);
      } else {
        toast.error(message);
        resetConfirmationPopupStates(true);
      }
    } catch (error) {
      toast.error("An error occurred while running QC");
      resetConfirmationPopupStates(true);
    } finally {
      setLoading(false);
    }
  }, [filesReady, searchValue, historyDate, order, tab, getFilesReady, resetConfirmationPopupStates]); // eslint-disable-line
  
  /**
   * Show confirmation modal to run workorder qc
   */
  const showQcConfirmation = useCallback((rerun) => {
    setRunQcType(rerun);
    setShowConfirmation(true);
    setFromRemoveClick(false);
    setConfirmationTitle(`Confirm ${rerun ? "ReRun" : "Run"} QC`);
    setConfirmationBodyComponent(() => () => (
      <div>
        You are about to {rerun ? "ReRun" : "Run"} QC for
        <b>
          {` ${counter} report${counter > 1 ? "s" : ""}`}
        </b>
      </div>
    ));
  }, [counter]);

  /**
   * Handle confirm button click on confirmation modal
   */
  const handleConfirmationOnConfirm = async () => {
    const autosend = confirmationType === "send_auto";
    if (autosend) {
      await handleSendingReports(true, true);
    } else {
      if (pendingMarkStatus !== null) {
        await batchUpdateReportStatus(pendingMarkStatus);
      } else if (runQcType !== null) {
        await runWororderQc(runQcType);
      } else {
        await handleRemovePendingConfirm();
      }
    }
  }

  /**
   * Reset status
   */
  const resetQCState = (resetCheckedReports = false) => {
    setSelectedReports({
      qcNotRun: [],
      qcFailed: [],
      qcUnknown: [],
      qcPassed: [],
    });
    setConfirmationType(""); // reset confirmation type for autosend
    setBadWoList([]);
    setPendingMarkStatus(null); // reset mark category
    if (resetCheckedReports) {
      setSelectedRowKeys([]);
      setCounter(0);
    }
  }

  /**
   * Cancel modal and uncheck incomplete work order reports
   */
  // const cancelModalAndUncheckIncomplete = useCallback(() => {
  //   console.log("badWoList: ", badWoList);
  //   // Uncheck reports from all incomplete work orders in one operation
  //   if (badWoList.length > 0) {
  //     setFilesReady(prevList => {
  //       const list = [...prevList];
  //       const newWoStatus = new Map(woStatus);
  //       const selectedItems = [...selectedRowKeys];

  //       // Process all incomplete work orders
  //       badWoList.forEach(woId => {
  //         const woObj = woStatus.get(woId);
  //         if (woObj) {
  //           list.forEach((report) => {
  //             if (report.wo === woId && report.checked) {
  //               const woStatusObj = newWoStatus.get(report.wo);
  //               if (woStatusObj) {
  //                 woStatusObj.reportsLeft += 1;
  //                 newWoStatus.set(report.wo, woStatusObj);
  //               }
  //               report.checked = false;

  //               const idx = selectedItems.indexOf(`${report.wo}-${report.reportID}`);
  //               if (idx > -1) {
  //                 selectedItems.splice(idx, 1);
  //               }
  //             }
  //           });
  //         }
  //       });

  //       // Update all states together
  //       setSelectedRowKeys(selectedItems);
  //       setCounter(selectedItems.length);
  //       setWoStatus(newWoStatus);

  //       return list;
  //     });
  //   }
  //   resetQCState();
  // }, [badWoList, woStatus, selectedRowKeys]); // eslint-disable-line

  /**
   * Check if any Hold/Retesting status report was selected
   */
  const hasPendingHoldRetestingReport = () => {
    const invalidReportStatus = [0, 2, 3]; // Pending, Hold, Retesting
    const invalidReportSelected = filesReady.find((file) => file.checked && invalidReportStatus.includes(file.approve_status));
    if (invalidReportSelected && tab !== "history") {
      const statusText = REPORT_STATUS_FLAGS[invalidReportSelected.approve_status]?.label;
      toast.info(`Please remove the '${statusText}' status report (${invalidReportSelected.reportID}) for WO (${invalidReportSelected.wo}) before sending.`);
      return true;
    }
    return false;
  }

  /**
   * Check QC status of reports
   */
  const checkQCReports = (showPopup) => {
    const list = filesReady.slice();
    // Used to populate data for display in the modal
    const qcNotRun = [];
    const qcFailed = [];
    const qcUnknown = [];
    const qcPassed = [];
    list.forEach((report) => {
      // Populate selectedReport arrays
      if (report.checked) {
        if (report.qc === 0 || report.qc === null) {
          qcNotRun.push(report);
        } else if (report.qc === 2) {
          qcFailed.push(report);
        } else if (report.qc === 3) {
          qcUnknown.push(report);
        } else {
          qcPassed.push(report);
        }
      }
    });

    setSelectedReports({
      qcNotRun,
      qcFailed,
      qcUnknown,
      qcPassed,
    });
    const showConfirmPopup = qcNotRun.length > 0 || qcFailed.length > 0 || qcUnknown.length > 0 || badWoList.length > 0;
    if (!showConfirmPopup && !showPopup) {
      handleSendingReports(true);
    }
    return showConfirmPopup;
  }

  const handleSelectedReportsBlocking = () => {
    const list = filesReady;
    const woList = []; // Create new array instead of mutating existing one

    list.forEach((report) => {
      if (report.checked && woStatus.get(report.wo)?.reportsLeft > 0) {
        if (woList.indexOf(report.wo) === -1) {
          woList.push(report);
        }
      }
    });

    setBadWoList(woList);
    return woList.length !== 0;
  }

  /**
   * handle send report api call
   * @param {boolean} userConfirmation user confirmation needed 
   */
  const handleSendingReports = async (userConfirmation, autosend=false) => {
    if (!autosend && hasPendingHoldRetestingReport()) {
      return;
    }
    if (!userConfirmation) {
      // check for bad work orders
      const showPopup =handleSelectedReportsBlocking();
      // check for QC status of reports
      checkQCReports(showPopup);
      return;
    } else {
      // User confirmed - proceed with sending
      let reports = [];
      if (!autosend) {
        const list = getSendList();
        reports = list.map((report) => ({
          report_id: report.reportID,
          report_type: report.type,
          esv_submission_type: report.esv_belongs,
        }));
      }

      // Set loading states
      setLoading(true);
      setSkeletonLoading(true);

      try {
        const frontend_log_data = {
          reports: keys(selectedReports).reduce((obj, key) => {
            if (selectedReports[key].length > 0) {
              obj[key] = selectedReports[key].map((report) => report.path);
            }
            return obj;
          }, {}),
        };
        const response = await sendReportServer(user.id, reports, tab, autosend, frontend_log_data);

        // Handle different response types
        if (response.success) {
          // Check for different response patterns
          const { uploaded_and_sent = [], failed = [] } = response;
          // Determine response type based on message and data
          let summaryData = {
            uploadedAndSent: uploaded_and_sent,
            failed: failed
          };
          // Update summary data for the modal
          setSendSummaryData(summaryData);

          // Show the summary modal
          setShowSendSummary(true);

          // Close the confirmation modal if it's open
          resetQCState(true);
          resetConfirmationPopupStates()

          // Refresh the data if not in history tab
          if (tab !== "history") {
            const input = searchValue;
            const date = tab === "history" ? historyDate : "";
            await getFilesReady(date, input, order, tab);
          }
        } else {
          const { message, uploaded_and_sent, failed } = response;
          // Show summary modal if there are failed reports
          if (failed && failed.length > 0) {
            let summaryData = {
              uploadedAndSent: uploaded_and_sent,
              failed: failed
            };
            setSendSummaryData(summaryData);
            setShowSendSummary(true);
          }

          toast.error(message);
          // Reset loading states
          setLoading(false);
          setSkeletonLoading(false);
          // Close the confirmation modal if it's open
          resetQCState();
          resetConfirmationPopupStates(true);
        }
      } catch (error) {
        console.error("Error sending reports:", error);
        toast.error("Something went wrong!");
        // Reset loading states
        setLoading(false);
        setSkeletonLoading(false);
        // Close the confirmation modal if it's open
        resetQCState();
        resetConfirmationPopupStates(true);
      } finally {
        setLoading(false);
        setSkeletonLoading(false);
      }
    }
  }

  /**
   * show confirmation modal for autosend
   */
  const handleSendingReportsAuto = () => {
    setConfirmationType("send_auto");
    setShowConfirmation(true);
    setFromRemoveClick(false);
    setConfirmationTitle("Send All Approved Reports");
    setConfirmationBodyComponent(() => () => (
      <span>You’re about to send all approved reports. Are you sure you want to continue?</span>
    ));
  }

  /**
   * Reset state aflter run qc function call
   */
  const resetStateAfterRunQC = async () => {
    const category = tab;
    resetQCState(false);
    await getFilesReady("", searchValue, order, category);
    setLoading(false);
    setSkeletonLoading(false);
  }

  /**
   * handle run qc function - calls both regular QC and AI QC
   * Senior approach: Robust error handling, proper state management, user feedback
   */
  const handleRunQC = async (report_id_list = []) => {
    // Set loading states
    setLoading(true);
    setSkeletonLoading(true);
    const controller = new AbortController();
    abortPrevApiCall(controller);

    const payload = {
      rerun: false,
    };
    if (report_id_list && report_id_list.length > 0) {
      payload.report_id_list = report_id_list;
    }
    const qcResponse = await runQCApiPost(payload);
    const qcSuccess = qcResponse.success;
    resetStateAfterRunQC();
    if (!qcSuccess) {
      toast.error(qcSuccess.message || "An unexpected error occurred during QC processing");
    }
  };

  /**
   * Report status filter handler
   */
  const handleStausChange = (selectedFilterValue) => {
    setSelectedFilter(selectedFilterValue);
  };

  /**
   * Report type filter handler
   */
  const handleReportTypeChange = (selectedReportTypeValue) => {
    setSelectedReportType(selectedReportTypeValue);
  };

  /**
   * QC status filter handler
   */
  // NOTE: Uncomment for qc filter
  // const handleQcChange = (selectedQcValue) => {
  //   setSelectedQc(selectedQcValue);
  // };

  /**
   * AI QC status filter handler
   */
  // NOTE: Uncomment for ai qc filter
  // const handleAiQcChange = (selectedAiQcValue) => {
  //   setSelectedAiQc(selectedAiQcValue);
  // };

  /**
   * AI Soft QC status filter handler
   */
  const handleMLSoftQcChange = (selectedMLSoftQcValue) => {
    setSelectedMLSoftQc(selectedMLSoftQcValue);
  };

  /**
   * Created by filter handler
   */
  const handleCreatedByChange = (selectedCreatedByValue) => {
    setSelectedCreatedBy(selectedCreatedByValue);
  };

  /**
   * Approved by filter handler
   */
  const handleApprovedByChange = (selectedApprovedByValue) => {
    setSelectedApprovedBy(selectedApprovedByValue);
  };

  /**
   * Sent by filter handler
   */
  const handleSentByChange = (selectedSentByValue) => {
    setSelectedSentBy(selectedSentByValue);
  };

  /**
   * Select current month
   */
  const handleCurrentMonthClick = useCallback(() => {
    if (datePickerRef.current) {
      datePickerRef.current.blur();
    }
    handleHistoryDate(getHistoryDates().startDate);
  }, [handleHistoryDate]);

  /**
   * Toggle modal for send report summary
   */
  const toggleSendSummaryModal = useCallback(() => {
    setShowSendSummary(!showSendSummary);
    setSendSummaryData({
      uploadedAndSent: [],
      failed: []
    });
    setLoading(false);
  }, [showSendSummary]);

  /**
   * Toggle modal for send report summary
   */
  const toggleUnchangedWOModal = useCallback(() => {
    setShowUnchangedWOModal(!showUnchangedWOModal);
    setUnchangedWoList([]);
    setLoading(false);
  }, [showUnchangedWOModal]);

  /**
   * Show confirmation dialog
   * @param {Number} new_report_status 
   */
  const showMarkConfirmation = (new_report_status) => {
    const status = REPORT_STATUS_FLAGS[new_report_status]?.label;

    if (new_report_status === 1) {
      const qcNotRun = filesReady.filter((file) => file.qc === 0 && file.checked === true);
      if (qcNotRun.length > 0) {
        setPendingMarkStatus(new_report_status);
        setSelectedReports({
          ...selectedReports,
          qcNotRun,
        })
        return;
      }
    }

    setPendingMarkStatus(new_report_status);
    setShowConfirmation(true);
    setFromRemoveClick(false);
    setConfirmationTitle("Confirm Status Change");
    setConfirmationBodyComponent(() => () => (
      <div>
        You are about to change the status of
        <b>
          {` ${counter} report${counter > 1 ? "s" : ""}`}
        </b>
        {" to "}
        <Tag className="SendReportConfirmationTag">{status}</Tag>
      </div>
    ));
  }

  /**
   * Handle "Pending", "Hold", "Available" buttons.
   * @param {String} category "pending", "hold", "available"
   * @returns {void}
   */
  const handleMove = async (category) => {
    const payload = [];
    const showPopup = handleSelectedReportsBlocking();

    if (!showPopup) {
      const sendList = getSendList();
      sendList.forEach((file) => {
        if (file.checked) {
          payload.push(file.reportID);
        }
      });
      if (payload.length < 1 || skeletonLoading) {
        toast.warning("Please Select a report");
        return;
      }
      const tabTitle = `${category[0].toUpperCase()}${category.slice(1)}`;
      setMoveCategory(category);
      setShowConfirmation(true);
      setFromRemoveClick(false);
      setConfirmationTitle("Confirm Move Report");
      setConfirmationBodyComponent(() => () => (
        <div>
          You are about to move
          <b>
            {` ${counter} report${counter > 1 ? "s" : ""}`}
          </b>
          {" to "}
          <Tag className="SendReportConfirmationTag">{tabTitle}</Tag>
          Tab.
        </div>
      ));
    }
  };

  /**
   * Handles removing reports
   */
  const handleRemove = async () => {
    // const showPopup = handleSelectedReportsBlocking(); // NOTE: Commented out to allow removal even when incomplete work orders are selected
    const payload = [];

    // if (!showPopup) {
    // For removal, we want to remove all checked reports regardless of work order completion status
    // So we collect all checked reports directly instead of using getSendList() which filters by work order status
    filesReady.forEach((file) => {
      if (file.checked) {
        payload.push(file.reportID);
      }
    });
    if (payload.length < 1 || skeletonLoading) {
      toast.warning("Please Select a report");
      return;
    }

    setShowConfirmation(true);
    setFromRemoveClick(true);
    setConfirmationTitle("Confirm Remove Report");
    setConfirmationBodyComponent(() => () => (
      <div>
        You are about to remove
        <b>
          {` ${counter} report${counter > 1 ? "s" : ""}`}
        </b>
      </div>
    ));
    // }
  };

  /**
   * handle checkbox change event
   * @param {string[]} newSelectedRowKeys 
   */
  const onCheckboxChange = useCallback((newSelectedRowKeys) => {
    // Update counter immediately to prevent negative values
    setCounter(newSelectedRowKeys.length || 0);
    setSelectedRowKeys(newSelectedRowKeys);

    // Sync filesReady state with new selectedRowKeys
    setFilesReady(prevList => {
      const list = [...prevList];
      const newWoStatus = new Map(woStatus);

      const selectedKeysSet = new Set(newSelectedRowKeys);

      list.forEach((report) => {
        const rowKey = `${report.wo}-${report.reportID}`;
        const shouldBeChecked = selectedKeysSet.has(rowKey);

        if (report.checked !== shouldBeChecked) {
          report.checked = shouldBeChecked;

          // Update woStatus
          const woObj = newWoStatus.get(report.wo);
          if (woObj) {
            woObj.reportsLeft = shouldBeChecked ? woObj.reportsLeft - 1 : woObj.reportsLeft + 1;
            newWoStatus.set(report.wo, woObj);
          }
        }
      });

      setWoStatus(newWoStatus);
      return list;
    });

  }, [woStatus]);


  const renderExtraFooter = useCallback(() => (
    <div className="ant-picker-footer SendReports__HistoryDatePickerFooter">
      <Button type="link" className="ant-picker-today-btn" onClick={handleCurrentMonthClick}>This Month</Button>
    </div>
  ), [handleCurrentMonthClick]);

  // Render method
  const { qcFailed, qcNotRun, qcUnknown } = selectedReports;

  // Memoize expensive calculations to prevent unnecessary re-computations
  const sendBtnAccess = useMemo(() =>
    (tab === "history" || (user.permissionMap?.send_sendreports)) && tab !== "hold",
    [tab, user]
  );

  const showActionPopover = useMemo(() =>
    ["available", "pending", "hold"].some((curTab) => curTab === tab),
    [tab]
  );

  const sendAutoBtnAccess = useMemo(() => user.permissionMap && user.permissionMap.sendauto_sendreports && tab === "available", [user, tab]);

  // Memoize table props to prevent unnecessary re-renders
  const tableProps = useMemo(() => ({
    tab,
    data: filesReady,
    loading: skeletonLoading || loading,
    sortCreatedBy: handleTimeSort,
    order,
    user,
    counter,
    onPreview: handlePreview,
    selectedRowKeys,
    onCheckboxChange: onCheckboxChange,
    onSelectChange: handleChangeCheckbox,
  }), [
    tab,
    filesReady,
    skeletonLoading,
    loading,
    handleTimeSort,
    order,
    user,
    counter,
    handlePreview,
    selectedRowKeys,
    onCheckboxChange,
    handleChangeCheckbox,
  ]);

  return (
    <>
      <div className="SendReportsServer__Header">
        <div className="SendReportsServer__Header__Autosend">
          {sendAutoBtnAccess && (
            <Button
              type="button"
              className="reportsReadyFooterSendBtn autosend"
              onClick={handleSendingReportsAuto}
              loading={loading}
              disabled={loading}
            >
              Autosend
            </Button>)
          }
        </div>
        <div className="SendReportsServer__Header__FilterContainer">
          <div className="SendReportsServer__Filters">
            {tab !== "pending" && tab !== "hold" && (
              <div className="SendReportsServer__Search">
                <label className="SendReportsServer__Search--label">Search</label>
                <SearchSendReports
                  handleSearchSubmit={handleSearchSubmit}
                  getFilesReady={getFilesReady}
                  date={historyDate}
                  category={tab}
                />
              </div>
            )}
            <FilterDropdown
              label="Test Category"
              withSearch
              allowClear
              options={testCategoriesOptions}
              value={selectedCategory}
              onSelect={filterByTestCategory}
              onClear={() => filterByTestCategory("")}
            />
            <FilterDropdown
              label="Report Status"
              defaultValue="All"
              allowClear
              value={selectedFilter}
              options={convertStatusMapToArrayOptions(REPORT_STATUS_FLAGS)}
              onSelect={(action) => handleStausChange(action)}
              onClear={() => handleStausChange("")}
            />
            <FilterDropdown
              label="Report Type"
              defaultValue="All"
              allowClear
              value={selectedReportType}
              options={reportTypeOptions}
              onSelect={(action) => handleReportTypeChange(action)}
              onClear={() => handleReportTypeChange("")}
            />
            {/* <FilterDropdown
              label="QC Status"
              defaultValue="All"
              options={SOFTWARE_QC_OPTIONS}
              onSelect={(action) => handleQcChange(action)}
            />
            <FilterDropdown
              label="AI QC Status"
              defaultValue="All"
              options={AI_QC_OPTIONS}
              onSelect={(action) => handleAiQcChange(action)}
            /> */}
            <FilterDropdown
              label="QC Status"
              defaultValue="All"
              allowClear
              value={selectedMLSoftQc}
              options={convertStatusMapToArrayOptions(ML_SOFTWARE_QC_FLAGS, [-1])}
              onSelect={(action) => handleMLSoftQcChange(action)}
              onClear={() => handleMLSoftQcChange("")}
            />
            {user.permissionMap.view_sendreports_created_by && (
              <FilterDropdown
                label="Created By"
                withSearch
                allowClear
                options={createByUserOptions}
                value={selectedCreatedBy}
                onSelect={(action) => handleCreatedByChange(action)}
                onClear={() => handleCreatedByChange("")}
              />
            )}
            {user.permissionMap.view_sendreports_approved_by && (
              <FilterDropdown
                label="Approved By"
                withSearch
                allowClear
                options={approvedByUserOptions}
                value={selectedApprovedBy}
                onSelect={(action) => handleApprovedByChange(action)}
                onClear={() => handleApprovedByChange("")}
              />
            )}
            {tab === "history" && (
              <FilterDropdown
                label="Sent By"
                withSearch
                allowClear
                options={createByUserOptions}
                value={selectedSentBy}
                onSelect={(action) => handleSentByChange(action)}
                onClear={() => handleSentByChange("")}
              />
            )}
            {tab === "history" && user.permissionMap.view_sendreports_history_date && (
              <DatePicker
                showNow={false}
                allowClear={false}
                value={historyDate}
                defaultValue={historyDate}
                className="SendReportsServer__HistoryDate"
                picker="month"
                format="MMMM YYYY"
                disabledDate={(current) => current.isAfter(dayjs())}
                onChange={(e) => handleHistoryDate(e)}
                renderExtraFooter={renderExtraFooter}
                ref={datePickerRef}
              />
            )}
            {sendBtnAccess && (
              <Button
                type="button"
                onClick={() => handleSendingReports(tab === "history")}
                className="reportsReadyFooterSendBtn"
                loading={skeletonLoading}
                disabled={skeletonLoading || counter === 0}
              >
                {`Send (${counter})`}
              </Button>
            )}
            {tab === "available" && (
              <Button
                type="button"
                onClick={() => handleRunQC()}
                className="reportsReadyFooterDownloadBtn"
                loading={skeletonLoading}
                disabled={skeletonLoading}
              >
                QC
              </Button>
            )}
            {user.permissionMap.download_sendreports && !showActionPopover && (
              <div className="reportsReadyFooter">
                {user.permissionMap.download_sendreports && (
                  <Button
                    type="button"
                    onClick={handleDownloadClick}
                    className="reportsReadyFooterDownloadBtn"
                    disabled={skeletonLoading || counter === 0}
                  >
                    {`Download (${counter})`}
                  </Button>
                )}
              </div>
            )}
          </div>
          <div className="SendReportsServer__ActionsPanel">
            {showActionPopover && (
              <ActionPopup
                tab={tab}
                counter={counter}
                user={user}
                loading={skeletonLoading}
                onDownloadClick={handleDownloadClick}
                showMarkConfirmation={showMarkConfirmation}
                showQcConfirmation={showQcConfirmation}
                handleMove={handleMove}
                onRemoveClick={handleRemove}
              />
            )}
          </div>
        </div>
      </div>
      <SendReportsTable {...tableProps} />
      {showSendSummary && (
        <SendReportSummaryModal
          title="Send Report Summary"
          uploadedAndSent={sendSummaryData.uploadedAndSent}
          failed={sendSummaryData.failed}
          toggleModal={toggleSendSummaryModal}
        />
      )}
      {showUnchangedWOModal && (
        <UnchangedWOModal
          woList={unchangedWoList}
          toggleModal={toggleUnchangedWOModal}
        />
      )}
      {showPreview && (
        <SendReportsPreview
          previewFile={previewFile}
          togglePreview={togglePreview}
          updateResultApi={updateReusltApi}
          generateReport={generateReport}
          updateStatusApi={updateStatusApi}
          handleApproveRejectSampleQC={handleApproveRejectSampleQC}
          currWoId={currWoId}
          enableEdit={enableEdit}
          tab={tab}
          woStatus={woStatus}
          testCategories={testCategories}
          user={user}
        />
      )}
      {((qcFailed.length > 0 || qcNotRun.length > 0 || qcUnknown.length > 0 || badWoList.length > 0) && tab !== "history") && (
        <SendReportsConfirmModal
          selectedReports={selectedReports}
          badWoList={badWoList}
          sendReports={handleSendingReports}
          runQC={handleRunQC}
          cancelModal={() => resetQCState()}
          loading={loading}
          qcLoading={skeletonLoading}
          pendingMarkStatus={pendingMarkStatus}
          batchUpdateReportStatus={batchUpdateReportStatus}
        />
      )}
      <ConfirmationModal
        open={showConfirmation}
        title={confirmationTitle}
        bodyComponent={
          loading && confirmationType === "send_auto"
            ? () => (
                <span>Reports are now being sent to customers. Please wait until the process is fully complete.</span>
              )
            : confirmationBodyComponent
        }
        okText={
          loading && confirmationType === "send_auto"
            ? "Sending..."
            : fromRemoveClick
            ? "Remove"
            : "Confirm"
        }
        cancelText="Cancel"
        onOk={handleConfirmationOnConfirm}
        onCancel={() => resetConfirmationPopupStates()}
        loading={loading}
      />
    </>
  );
};

export default SendReportsServer;
